<script setup>
import {
  computed,
  onMounted,
  useTemplateRef,
  ref,
  getCurrentInstance,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import AttachmentsAPI from 'dashboard/api/attachments';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { LocalStorage } from 'shared/helpers/localStorage';

const { attachment, showTranscribedText } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const AUDIO_PLAYBACK_RATE_STORAGE_KEY = 'cw_audio_playback_rate';
const AUDIO_VOLUME_STORAGE_KEY = 'cw_audio_volume_level';
const TRANSCRIPT_PREVIEW_LENGTH = 200;

const { t } = useI18n();

const getStoredPlaybackSpeed = () => {
  const speed = Number(LocalStorage.get(AUDIO_PLAYBACK_RATE_STORAGE_KEY));
  return PLAYBACK_SPEEDS.includes(speed) ? speed : 1;
};

const getStoredVolumeLevel = () => {
  const volume = Number(LocalStorage.get(AUDIO_VOLUME_STORAGE_KEY));
  return Number.isFinite(volume) && volume >= 0 && volume <= 1 ? volume : 1;
};

const timeStampURL = computed(() => {
  return timeStampAppendedURL(attachment.dataUrl);
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(getStoredPlaybackSpeed());
const volumeLevel = ref(getStoredVolumeLevel());
const isTranscribing = ref(false);
const transcribedTextOverride = ref('');
const isTranscriptExpanded = ref(false);

const { uid } = getCurrentInstance();

const normalizedTranscribedText = computed(() => {
  return (
    transcribedTextOverride.value ||
    attachment.transcribedText ||
    attachment.transcribed_text ||
    ''
  )
    .toString()
    .trim();
});

const transcriptionState = computed(() => {
  if (!showTranscribedText) return 'hidden';
  if (normalizedTranscribedText.value) return 'ready';
  if (isTranscribing.value) return 'pending';

  return 'idle';
});

const isTranscriptLong = computed(() => {
  return normalizedTranscribedText.value.length > TRANSCRIPT_PREVIEW_LENGTH;
});

const displayedTranscript = computed(() => {
  const text = normalizedTranscribedText.value;
  if (!isTranscriptLong.value || isTranscriptExpanded.value) return text;
  return `${text.slice(0, TRANSCRIPT_PREVIEW_LENGTH).trimEnd()}…`;
});

// MediaRecorder-produced WebM/Opus blobs lack a Duration header -> <audio>.duration
// resolves to Infinity until we seek past the end, which forces the engine to
// scan the file and compute the real length. Safe no-op for files with a real
// duration already (mp3/m4a/etc).
const resolveStreamingDuration = () => {
  const el = audioPlayer.value;
  if (!el) return;
  const onTimeUpdate = () => {
    el.removeEventListener('timeupdate', onTimeUpdate);
    el.currentTime = 0;
    duration.value = el.duration;
  };
  el.addEventListener('timeupdate', onTimeUpdate);
  try {
    el.currentTime = Number.MAX_SAFE_INTEGER;
  } catch {
    el.removeEventListener('timeupdate', onTimeUpdate);
  }
};

const transcribeAudio = async () => {
  try {
    isTranscribing.value = true;
    const response = await AttachmentsAPI.transcribe(attachment.id);
    const transcribedText =
      response?.data?.payload?.transcribed_text ||
      response?.data?.payload?.transcribedText ||
      '';

    transcribedTextOverride.value = transcribedText.toString().trim();

    if (!transcribedTextOverride.value) {
      useAlert(t('CONVERSATION.CONTEXT_MENU.TRANSCRIBE_AUDIO_ERROR'));
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('CONVERSATION.CONTEXT_MENU.TRANSCRIBE_AUDIO_ERROR')
    );
  } finally {
    isTranscribing.value = false;
  }
};

const playbackSpeedOptions = computed(() => {
  return PLAYBACK_SPEEDS.map(speed => ({
    value: speed,
    label: `${speed}x`,
  }));
});

const playbackSpeedLabel = computed(() => {
  const activeOption = playbackSpeedOptions.value.find(
    option => option.value === playbackSpeed.value
  );
  return activeOption?.label || '1x';
});

const onLoadedMetadata = () => {
  const d = audioPlayer.value?.duration;
  if (!Number.isFinite(d)) {
    resolveStreamingDuration();
    return;
  }
  duration.value = d;
};

onMounted(() => {
  const d = audioPlayer.value?.duration;
  if (Number.isFinite(d)) duration.value = d;
  audioPlayer.value.playbackRate = playbackSpeed.value;
  audioPlayer.value.volume = volumeLevel.value;
  audioPlayer.value.muted = volumeLevel.value === 0;
  isMuted.value = audioPlayer.value.muted;
});

useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const volumeIcon = computed(() => {
  if (isMuted.value || volumeLevel.value === 0) {
    return 'i-lucide-volume-off';
  }

  if (volumeLevel.value < 0.5) {
    return 'i-lucide-volume-1';
  }

  return 'i-lucide-volume-2';
});

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
};

const setPlaybackSpeed = speed => {
  playbackSpeed.value = speed;
  LocalStorage.set(AUDIO_PLAYBACK_RATE_STORAGE_KEY, playbackSpeed.value);
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const cyclePlaybackSpeed = () => {
  const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed.value);
  const nextIndex =
    currentIndex === -1 ? 0 : (currentIndex + 1) % PLAYBACK_SPEEDS.length;
  setPlaybackSpeed(PLAYBACK_SPEEDS[nextIndex]);
};

const changeVolume = event => {
  const nextVolume = Number(event.target.value);
  volumeLevel.value = nextVolume;
  audioPlayer.value.volume = nextVolume;
  audioPlayer.value.muted = nextVolume === 0;
  isMuted.value = audioPlayer.value.muted;
  LocalStorage.set(AUDIO_VOLUME_STORAGE_KEY, nextVolume);
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
  >
    <source :src="timeStampURL" />
  </audio>
  <div
    v-bind="$attrs"
    class="rounded-xl w-full p-1.5 bg-transparent flex items-center gap-1.5"
  >
    <button class="p-0 border-0 size-8 shrink-0" @click="playOrPause">
      <Icon
        v-if="isPlaying"
        class="size-8"
        icon="i-teenyicons-pause-small-solid"
      />
      <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
    </button>
    <div class="tabular-nums text-xs shrink-0">
      {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
    </div>
    <div class="flex-1 items-center flex px-1 min-w-0">
      <input
        type="range"
        min="0"
        :max="duration"
        :value="currentTime"
        class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
        @input="seek"
      />
    </div>
    <div class="flex items-center gap-1 shrink-0">
      <button
        class="speed-button"
        :aria-label="$t('CONVERSATION.REPLYBOX.AUDIO_PLAYBACK_SPEED')"
        :title="$t('CONVERSATION.REPLYBOX.AUDIO_PLAYBACK_SPEED')"
        @click="cyclePlaybackSpeed"
      >
        <span class="speed-button__label">{{ playbackSpeedLabel }}</span>
      </button>
      <div class="volume-control">
        <button
          class="p-0 border-0 size-8 grid place-content-center shrink-0 rounded-full bg-n-alpha-2 hover:bg-n-alpha-3"
          :aria-label="$t('CONVERSATION.REPLYBOX.AUDIO_VOLUME')"
          @click="toggleMute"
        >
          <Icon :icon="volumeIcon" class="size-4" />
        </button>
        <div class="volume-popover">
          <div class="volume-popover__slider">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              :value="volumeLevel"
              class="volume-range"
              :aria-label="$t('CONVERSATION.REPLYBOX.AUDIO_VOLUME')"
              @input="changeVolume"
            />
          </div>
        </div>
      </div>
      <button
        class="p-0 border-0 size-8 grid place-content-center shrink-0"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>
  </div>
  <div
    v-if="transcriptionState === 'ready'"
    class="mt-2 w-full break-words rounded-lg bg-n-alpha-1 p-3 text-sm text-n-slate-12"
  >
    <p class="mb-1 text-[11px] font-medium uppercase tracking-[0.08em] text-n-slate-10">
      {{ t('CONVERSATION.REPLYBOX.AUDIO_TRANSCRIPTION_LABEL') }}
    </p>
    <p class="mb-0 whitespace-pre-wrap">
      {{ displayedTranscript }}
    </p>
    <button
      v-if="isTranscriptLong"
      class="block mt-1 p-0 border-0 bg-transparent text-n-slate-11 hover:text-n-slate-12 font-medium"
      @click="isTranscriptExpanded = !isTranscriptExpanded"
    >
      {{
        isTranscriptExpanded
          ? $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_LESS')
          : $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_MORE')
      }}
    </button>
  </div>
  <div
    v-else-if="transcriptionState === 'pending'"
    class="mt-1 flex w-full items-center justify-end gap-1.5 pe-8 text-[11px] text-n-slate-10"
  >
    <Icon icon="i-lucide-loader-circle" class="size-4 shrink-0 animate-spin" />
    <span>{{ t('CONVERSATION.REPLYBOX.AUDIO_TRANSCRIPTION_PENDING') }}</span>
  </div>
  <div
    v-else-if="transcriptionState === 'idle'"
    class="mt-1 flex w-full justify-end pe-8"
  >
    <button
      type="button"
      class="border-0 bg-transparent p-0 text-[11px] font-medium leading-none text-n-slate-10 hover:text-n-slate-11 hover:underline"
      @click="transcribeAudio"
    >
      {{ t('CONVERSATION.REPLYBOX.AUDIO_TRANSCRIBE_ACTION') }}
    </button>
  </div>
</template>

<style lang="scss" scoped>
.volume-control {
  @apply relative shrink-0;

  &:hover .volume-popover,
  &:focus-within .volume-popover {
    @apply opacity-100 pointer-events-auto translate-y-0;
  }
}

.volume-popover {
  @apply absolute left-1/2 -translate-x-1/2 rounded-xl border border-n-container bg-n-alpha-white shadow-[0px_8px_24px_0px_rgba(15,23,42,0.18)] opacity-0 pointer-events-none transition-all duration-150 ease-out;
  bottom: calc(100% - 0.25rem);
}

.volume-popover__slider {
  @apply flex items-center justify-center w-12 h-36 p-2;
}

.volume-range {
  @apply w-24 h-1 appearance-none cursor-pointer rounded-lg bg-n-slate-12/40 accent-current;
  transform: rotate(-90deg);
}

.speed-button {
  @apply inline-flex items-center justify-center min-w-[2.75rem] h-7 px-2 rounded-2xl border-0 bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 transition-colors;
}

.speed-button__label {
  @apply text-xs font-medium;
}
</style>
